import React, { useState, type JSX } from "react";
import {Menu, MenuItem, Grid, Button} from "@mui/material";
import {fetchDownloadFile} from "../../../util/FileUtils";
import DownloadCsvIcon from "../../icons/DownloadCsvIcon";
import {getErrorMessageForFileDownloads} from "../../../util/NetworkErrorUtil";
import {useSnackbar} from "../../../context/snackbar-context";
import {LabelValue} from "../../../types/common/LabelValue";

interface DownloadGroupSummaryCSVButtonProps {
  /** Key-value pairs appended as query parameters when building the download URL. */
  queryParams: Record<string, unknown>;
  /** Optional callback to handle download errors; falls back to snackbar when omitted. */
  onError?: (error: Error) => void;
  /** Options rendered in the drop-down; each option's value is appended as a `groupBy` query parameter. */
  groupByOptions: LabelValue[];
  /** Returns the download URL given the full set of query params. */
  getUrl: (queryParams: Record<string, unknown>) => string;
  /** Whether the button is disabled. */
  disabled?: boolean;
}

/**
 * Drop-down menu that allows the user to download a CSV file containing group summaries.
 *
 * @param queryParams List of key value pairs to be used as query parameters to generate the selected option's download URL
 * @param onError Callback function to handle errors that occur during the download process
 * @param groupByOptions List of options to be displayed in the menu. The value of each option will be used as the groupBy parameter in the download URL
 * @param getUrl Function that returns the download URL for the selected groupBy option.
 * @param disabled Whether the button is disabled
 * @returns {JSX.Element}
 */
const DownloadGroupSummaryCSVButton = ({
                                         queryParams,
                                         onError,
                                         groupByOptions,
                                         getUrl,
                                         disabled = false
                                       }: DownloadGroupSummaryCSVButtonProps): JSX.Element => {

  const {snackbarShowMessage} = useSnackbar();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const menuOpen = Boolean(menuAnchorEl);

  const handleClickGroupSummaryButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (groupByOptions.length > 1) {
      setMenuAnchorEl(event.currentTarget);
    } else {
      handleExportByGroup(groupByOptions[0].value);
    }
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleExportByGroup = async (groupByParameter: string) => {
    if (queryParams && groupByParameter) {
      const groupByQueryParams = { ...queryParams, groupBy: groupByParameter };
      const url = getUrl(groupByQueryParams);

      downloadFile(url);
    }
  };

  const handleError = (error: Error) => {
    if (onError) {
      onError(error);
    } else {
      snackbarShowMessage(getErrorMessageForFileDownloads(error), 'error');
    }
  };

  const downloadFile = async (reportUrl: string) => {
    fetchDownloadFile(reportUrl, undefined, handleError);
  };

  return (
    <Grid item>
      <Grid item>
        <Button variant={"contained"} startIcon={<DownloadCsvIcon/>} onClick={handleClickGroupSummaryButton} disabled={disabled}>
          Export Group Summaries
        </Button>
      </Grid>
      <Menu open={menuOpen} anchorEl={menuAnchorEl} onClose={handleCloseMenu}>
        {
          groupByOptions.map((groupByOption) =>
            <MenuItem key={groupByOption.value} onClick={() => handleExportByGroup(groupByOption.value)}>
              {groupByOption.label}
            </MenuItem>
          )
        }
      </Menu>
    </Grid>
  );
};

export default DownloadGroupSummaryCSVButton;
